<template>
  <div class="price-terms">
    <h4 class="font-serif mb-3">
      {{ $t("certificateHistory.overview") }}
    </h4>
    <canvas ref="chartRef"></canvas>
  </div>
</template>

<script setup lang="ts">
import Chart from "@/chart-setup";
import { PALETTE } from "@/composables/chartPalette";
import { formatShortMonthYear } from "@/composables/formatUtils";
import { onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import type { Certificate } from "../../types";

const props = defineProps<{
  certificates: Certificate[];
}>();

const { t } = useI18n();
const chartRef = useTemplateRef<HTMLCanvasElement>("chartRef");
let chartInstance: Chart<"bar", number[], string> | null = null;

const initializeChart = () => {
  if (chartRef.value) {
    const ctx = chartRef.value.getContext("2d");
    if (ctx) {
      const dataset = preprocessData();

      if (chartInstance) {
        chartInstance.destroy();
      }

      chartInstance = new Chart(chartRef.value, {
        type: "bar",
        data: dataset,
        options: {
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context: any) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.y !== null) {
                    label += context.parsed.y;
                  }
                  return label;
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              title: {
                display: true,
                text: t("certificateHistory.productionPeriod"),
              },
            },
            y: {
              stacked: true,
              title: {
                display: true,
                text: t("certificateHistory.certificateCount"),
              },
            },
          },
        },
      });
    }
  }
};

const preprocessData = () => {
  const groupedData: {
    [yearMonth: string]: {
      olderThan12Months: number;
      withTransactionId: number;
      regular: number;
    };
  } = {};

  // First, create entries for the last 12 months using YYYY-MM format (always sortable)
  const today = new Date();
  for (let i = 11; i >= 0; i--) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const yearMonth = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}`;
    groupedData[yearMonth] = {
      olderThan12Months: 0,
      withTransactionId: 0,
      regular: 0,
    };
  }

  // Then add the certificate data
  props.certificates.forEach((certificate) => {
    const productionDate = new Date(certificate.production_month);
    const yearMonth = `${productionDate.getFullYear()}-${String(
      productionDate.getMonth() + 1
    ).padStart(2, "0")}`;

    if (!groupedData[yearMonth]) {
      groupedData[yearMonth] = {
        olderThan12Months: 0,
        withTransactionId: 0,
        regular: 0,
      };
    }

    if (certificate.transfer_deadline_passed) {
      groupedData[yearMonth].olderThan12Months += certificate.count;
    } else if (certificate.sales_transaction_item_id) {
      groupedData[yearMonth].withTransactionId += certificate.count;
    } else {
      groupedData[yearMonth].regular += certificate.count;
    }
  });

  // Sort keys chronologically (YYYY-MM format sorts naturally)
  const sortedKeys = Object.keys(groupedData).sort();

  // Create display labels from sorted keys
  const sortedLabels = sortedKeys.map((yearMonth) => {
    const [year, month] = yearMonth.split("-");
    return formatShortMonthYear(
      new Date(parseInt(year), parseInt(month) - 1, 1)
    );
  });

  const dataOlderThan12Months = sortedKeys.map(
    (yearMonth) => groupedData[yearMonth].olderThan12Months
  );
  const dataWithTransactionId = sortedKeys.map(
    (yearMonth) => groupedData[yearMonth].withTransactionId
  );
  const dataRegular = sortedKeys.map(
    (yearMonth) => groupedData[yearMonth].regular
  );

  return {
    labels: sortedLabels,
    datasets: [
      {
        label: t("certificateHistory.expired"),
        data: dataOlderThan12Months,
        backgroundColor: PALETTE.semanticOverrides.certificateStatus.Expired,
        borderColor: PALETTE.semanticOverrides.certificateStatus.Expired,
        borderWidth: 2,
      },
      {
        label: t("certificateHistory.sold"),
        data: dataWithTransactionId,
        backgroundColor: PALETTE.semanticOverrides.certificateStatus.Sold,
        borderColor: PALETTE.semanticOverrides.certificateStatus.Sold,
        borderWidth: 2,
      },
      {
        label: t("certificateHistory.awaitingSale"),
        data: dataRegular,
        backgroundColor:
          PALETTE.semanticOverrides.certificateStatus["Awaiting Sale"],
        borderColor:
          PALETTE.semanticOverrides.certificateStatus["Awaiting Sale"],
        borderWidth: 2,
      },
    ],
  };
};

onMounted(() => {
  initializeChart();
});
</script>
